import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@services/http/client';
import { forwardingApi } from '@services/api/forwarding';
import { queryKeys } from '@services/api/queryKeys';
import type { ForwardingLog, Paginated, SenderBlock } from '@app-types/index';
import type {
  CreateSenderBlockInput,
  ForwardingLogParams,
  SenderBlockListParams,
} from '@services/api/forwarding';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------
export const useForwardingLogs = (
  params: ForwardingLogParams = {},
): UseQueryResult<Paginated<ForwardingLog>, ApiError> =>
  useQuery({
    queryKey: queryKeys.forwarding.logs(params),
    queryFn: () => forwardingApi.logs(params),
  });

export const useSenderBlocks = (
  params: SenderBlockListParams = {},
): UseQueryResult<Paginated<SenderBlock>, ApiError> =>
  useQuery({
    queryKey: queryKeys.forwarding.senderBlocks(params),
    queryFn: () => forwardingApi.senderBlocks(params),
  });

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------
export const useCreateSenderBlock = (): UseMutationResult<
  SenderBlock,
  ApiError,
  CreateSenderBlockInput
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSenderBlockInput) => forwardingApi.createSenderBlock(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.forwarding.senderBlockLists() });
      void qc.invalidateQueries({ queryKey: queryKeys.forwarding.senderLists() });
    },
  });
};

export const useDeleteSenderBlock = (): UseMutationResult<void, ApiError, string> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => forwardingApi.deleteSenderBlock(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.forwarding.senderBlockLists() });
      void qc.invalidateQueries({ queryKey: queryKeys.forwarding.senderLists() });
    },
  });
};
